import { Button, Checkbox, styled, Tooltip } from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";
import CustomDataGrid from "../../../common/js/components/CustomDataGrid";
import { Landvision } from "../../../common/js/icons";
import { noPhotoUrl, pageSize } from "../utils/constants";
import { buildAddress, formatDate, navigateToLandvision, toSqft } from "../utils/operations";
import { getButtonActionPropertiesForRoleAndStatus } from "./reviewCompsTables";
import { getCompInfo } from "./utils";

const StyledLink = styled(Link)({
    textDecoration: "none",
    lineHeight: "1.2",
});

const StyledDealName = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    wordWrap: "break-word",
    whiteSpace: "normal",
    lineHeight: "1.3",
});

const StyledDealCell = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const StyledAddress = styled("div")({
    fontSize: "12px",
});

const StyledLightButton = styled(Button)(({ theme }) => ({
    fontWeight: 400,
    minWidth: 0,
    margin: 0,
    marginRight: "5px",
    padding: "5px 10px",
    color: theme.palette.primary.main,
}));

const StyledCheckbox = styled(Checkbox)({
    transform: "scale(1.0)",
    padding: "0px !important",
    "&.MuiCheckbox-indeterminate svg": {
        color: "#2C99D2 !important",
    },
});

const StyledConfidentialName = styled("div")({
    color: "red",
    fontWeight: "bold",
});

const MyCompsDataGrid = (props) => {
    const {
        brokerComps,
        comps,
        compsPage,
        compTab,
        getComps,
        isLoading,
        handleCheckboxClick,
        role,
        setCompsPage,
        setCompsSort,
        setSelectBrokerCompsForMyComps,
        showSelectedItemsOnly,
        selectedBrokerCompsForMyComps,
        clientSideSortModel,
        setClientSideSortModel,
    } = props;

    const [serverSideSortModel, setServerSideSortModel] = useState([]);

    const brokerCompsData = comps?.broker_comps ?? [];
    const myCompsData = showSelectedItemsOnly ? selectedBrokerCompsForMyComps : (brokerCompsData?.content ?? []);
    const totalCount = showSelectedItemsOnly ? myCompsData.length : (brokerCompsData?.totalElements ?? 0);
    const gridPageSize = showSelectedItemsOnly ? myCompsData.length : pageSize;
    const currentPage = showSelectedItemsOnly ? 1 : compsPage;
    const sort = showSelectedItemsOnly ? clientSideSortModel : serverSideSortModel;

    const renderBrokers = (brokers) => {
        if (!brokers) return "";
        const brokerNames = brokers.split(",");
        if (brokerNames.length > 1) {
            return (
                <Tooltip title={brokers} placement="top">
                    <span>{`${brokerNames.length} Brokers`}</span>
                </Tooltip>
            );
        }
        return brokers;
    };

    const renderActionButton = (comp) => {
        const buttonProperties = getButtonActionPropertiesForRoleAndStatus(role, comp.compStatus);
        return (
            <StyledLink to={`/comp/${comp.id}`}>
                <Button variant={buttonProperties.variant} color="primary" onClick={() => null}>
                    {buttonProperties.text}
                </Button>
            </StyledLink>
        );
    };

    const columns = [
        {
            field: "dealKey",
            headerName: "",
            width: 5,
            sortable: false,
            renderHeader: () => {
                const allSelected = myCompsData.length > 0 && myCompsData.every((row) => brokerComps.get(row.dealKey));
                const someSelected = myCompsData.some((row) => brokerComps.get(row.dealKey));
                return (
                    <StyledCheckbox
                        className={allSelected ? "compCheckboxAllSelectedMap" : "compCheckboxAllMap"}
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => {
                            myCompsData.forEach((row) => {
                                const isCurrentlySelected = brokerComps.get(row.dealKey);
                                if (e.target.checked !== isCurrentlySelected) {
                                    handleCheckboxClick(row.dealKey, compTab);
                                }
                                setSelectBrokerCompsForMyComps(row, e.target.checked);
                            });
                        }}
                    />
                );
            },
            renderCell: (params) => (
                <StyledCheckbox
                    className={
                        brokerComps.get(params.row?.dealKey) ? "compCheckboxAllSelectedMap" : "compCheckboxAllMap"
                    }
                    checked={brokerComps.get(params.row?.dealKey)}
                    onChange={(e) => {
                        const dealKey = params.row?.dealKey;
                        handleCheckboxClick(dealKey, compTab);
                        setSelectBrokerCompsForMyComps(params.row, e.target.checked);
                    }}
                />
            ),
        },
        {
            field: "propertyPhoto",
            headerName: "Property",
            flex: 1,
            minWidth: 105,
            sortable: false,
            renderCell: (params) => (
                <div
                    className="propertySmallImageContainer"
                    style={{
                        backgroundImage: `url(${params.row?.propertyPhoto ?? params.row?.listingPhoto ?? noPhotoUrl})`,
                        marginLeft: "7px",
                        width: "85px",
                        minWidth: "85px",
                        height: "85px",
                    }}
                ></div>
            ),
        },
        {
            field: "listingName",
            headerName: "Address",
            flex: 2,
            minWidth: 165,
            renderCell: (params) => (
                <StyledDealCell>
                    {params.row?.confidentialFlg ? (
                        <StyledConfidentialName>Confidential</StyledConfidentialName>
                    ) : (
                        <StyledLink to={`/comp/${params.id}`} target="_blank">
                            <StyledDealName>{`${params.row?.listingName}`}</StyledDealName>
                        </StyledLink>
                    )}
                    <StyledAddress>{buildAddress(params.row?.primaryAddress ?? {})}</StyledAddress>
                    <div>{getCompInfo(params.row)}</div>
                </StyledDealCell>
            ),
        },
        {
            field: "brokers",
            headerName: "Brokers",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => renderBrokers(params.row?.brokers),
        },
        {
            field: "propertyType",
            headerName: "Type",
            flex: 1,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => `${params.row?.propertyType} ${params.row?.transactionType}`,
        },
        {
            field: "transactionDate",
            headerName: "Date Signed",
            flex: 1,
            minWidth: 90,
            sortComparator: (dateA, dateB) => {
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return new Date(dateA) - new Date(dateB);
            },
            renderCell: (params) => {
                return params.row?.transactionDate ? formatDate("MM-DD-YYYY")(params.row.transactionDate) : "N/A";
            },
        },
        {
            field: "sortableRatePrice",
            headerName: "Rate/Price",
            flex: 1,
            minWidth: 115,
            sortComparator: (rp1, rp2) => {
                const extractNumber = (str) => {
                    if (!str || str === "-" || str === "Call Broker") return 0;
                    const match = str.match(/\$([\d,]+\.?\d*)/);
                    return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
                };
                return extractNumber(rp1) - extractNumber(rp2);
            },
            renderCell: (params) => {
                return params.row?.sortableRatePrice;
            },
        },
        {
            field: "squareFeet",
            headerName: "Sqft",
            flex: 1,
            minWidth: 100,
            sortComparator: (sqft1, sqft2) => {
                const parseNumber = (val) => {
                    if (!val) return 0;
                    return typeof val === "number" ? val : parseFloat(val.toString().replace(/[^\d.]/g, "")) || 0;
                };
                return parseNumber(sqft1) - parseNumber(sqft2);
            },
            renderCell: (params) => toSqft(params.row?.squareFeet),
        },
        {
            field: "propertyKey",
            headerName: "Link",
            sortable: false,
            flex: 1,
            minWidth: 50,
            renderCell: (params) => {
                return (
                    <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                        <Tooltip title="Landvision" placement="top">
                            <span>
                                <StyledLightButton
                                    onClick={() =>
                                        navigateToLandvision(params.row?.propertyKey, params.row?.listingKey)
                                    }
                                    color="primary"
                                >
                                    <Landvision fill="#2C99D2" />
                                </StyledLightButton>
                            </span>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: "compStatus",
            headerName: "action",
            flex: 1,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => renderActionButton(params.row),
        },
    ];

    const handlePageChange = (value) => {
        setCompsPage(value);
        if (!showSelectedItemsOnly) {
            getComps(compTab);
        }
    };

    const handleSortModelChange = (newSortModel) => {
        if (!showSelectedItemsOnly) {
            setCompsPage(1);
            setServerSideSortModel(newSortModel);
            const newSort =
                newSortModel[0]?.field && newSortModel[0]?.sort
                    ? `${newSortModel[0].field},${newSortModel[0].sort}`
                    : null;
            setCompsSort(newSort);
            getComps(compTab);
        } else {
            setClientSideSortModel(newSortModel);
        }
    };

    return (
        <CustomDataGrid
            rows={myCompsData}
            columns={columns}
            totalCount={totalCount}
            page={currentPage}
            rowHeight={100}
            pageSize={gridPageSize}
            serverSideSortModel={sort}
            onSortModelChange={handleSortModelChange}
            onPageChange={(_, value) => handlePageChange(value)}
            loading={isLoading}
            clientSidePagination={showSelectedItemsOnly}
        />
    );
};

export default MyCompsDataGrid;
